/**
 * Package Line-up + Request Upgrade — Master Addendum · Feb 2026.
 *
 * Renders below the current Contracted Service Package card on the PM
 * dashboard. Shows every NECS Collection (Multifamily + 12 commercial
 * verticals) with the full service list — no "Everything in X" shims.
 * The Request Upgrade button on any tier opens an in-app dialog with a
 * required Preferred Starting Date + Alternative Services picker; on
 * submit the payload posts to /api/leads/inbound-call with
 * source="app" + kind="upgrade_request" so the NECS Admin CRM picks it
 * up and can send back a revised quote for approval.
 */
import { useState, useMemo } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { CheckCircle, ChevronDown, ChevronUp, Package } from 'lucide-react';
import { toast } from 'sonner';
import { packageDetails, getFlattenedServices, getCanonicalPackageName } from '../lib/packageDetails';
import { usePropertyPackages } from '../hooks/usePropertyPackages';

const API = process.env.REACT_APP_BACKEND_URL;

// Order the tiers so the ladder reads Essential → Presidential.
const TIER_ORDER = [
  'Essential Collection',
  'Professional Collection',
  'Elite Collection',
  'Signature Collection',
  'Noble Empire Executive Collection',
  'Noble Empire Presidential Collection',
];

// Alternative services the client can add on top of their contracted tier.
// Aligned with the addendum service catalog + PM Service Request list.
const ALTERNATIVE_SERVICES = [
  'Bulk Removal',
  'Pressure Washing',
  'Dumpster & Trash Room Care',
  'Window Cleaning (Interior + Exterior)',
  'Eco-Friendly Amenities Cleaning',
  'Deep Cleaning',
  'Carpet Cleaning & Extraction',
  'Floor Care',
  'Floor Polishing',
  'Floor Restoration',
  'Day Porter Services',
  'Sanitation / Disinfection',
  'Recycling Program Expansion',
  'Property Appearance Patrol',
];

const BUSINESS_TYPE_LABELS = {
  multifamily:  'Multifamily Community',
  office:       'Office Building',
  retail:       'Retail Plaza',
  gas_station:  'Gas Station',
  school:       'School / Campus',
  medical:      'Medical Clinic',
  warehouse:    'Warehouse / Industrial',
  restaurant:   'Restaurant',
  church:       'Church / Worship',
  mall:         'Mall',
  bank:         'Bank',
  stadium:      'Stadium / Arena',
  airport:      'Airport',
};

function inferBusinessType(property) {
  if (!property) return 'multifamily';
  const t = (property.property_type || '').toString().toLowerCase();
  if (BUSINESS_TYPE_LABELS[t]) return t;
  if (property.is_commercial || property.is_cleaners_demo) return t || 'office';
  return 'multifamily';
}

export default function PackageLineupSection({ property, user }) {
  const [openTier, setOpenTier] = useState(null);   // tier expanded to see services
  const [upgradeTier, setUpgradeTier] = useState(null); // tier selected for Request Upgrade dialog
  const businessType = inferBusinessType(property);
  const businessLabel = BUSINESS_TYPE_LABELS[businessType] || 'Multifamily Community';
  const packages = usePropertyPackages(property);
  const currentPkg = packages.current.name;

  const tiers = packages.tiers.map(tier => ({ ...tier, isCurrent: tier.name === currentPkg }));

  return (
    <>
      <Card
        className="p-6 mb-8 border-2"
        style={{ borderColor: '#3D3D3D', backgroundColor: '#1a1a1a' }}
        data-testid="pm-package-lineup"
      >
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] mb-1" style={{ color: '#C9A962', fontFamily: 'Barlow Condensed, sans-serif' }}>
              Full NECS Package Line-up
            </p>
            <h3 className="text-xl font-bold text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              Recommended for {businessLabel}
            </h3>
          </div>
          <Badge className="bg-[#C9A962] text-black font-bold tracking-widest text-[10px]">
            Business type · {businessType}
          </Badge>
        </div>

        <div className="space-y-3" data-testid="pm-package-lineup-list">
          {packages.loading && <p className="text-sm text-zinc-300" role="status" data-testid="pm-package-lineup-loading">Loading approved packages…</p>}
          {packages.error && <p className="text-sm text-red-300" role="alert" data-testid="pm-package-lineup-error">{packages.error}</p>}
          {tiers.map((tier) => {
            const expanded = openTier === tier.name;
            return (
              <div
                key={tier.name}
                className="rounded-lg border"
                style={{ borderColor: tier.isCurrent ? '#FFD60A' : '#3D3D3D', backgroundColor: '#0f0f0f' }}
                data-testid={`pm-package-tier-${tier.name.replace(/\s+/g, '-').toLowerCase()}`}
              >
                <button
                  type="button"
                  onClick={() => setOpenTier(expanded ? null : tier.name)}
                  className="w-full flex items-center justify-between gap-3 p-4 text-left"
                  data-testid={`pm-package-expand-${tier.key || tier.name.replace(/\s+/g, '-').toLowerCase()}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <Package className="h-5 w-5 flex-shrink-0" style={{ color: tier.isCurrent ? '#FFD60A' : '#C9A962' }} />
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-white break-words">
                        {tier.name}
                        {tier.isCurrent && (
                          <span className="ml-2 inline-block px-1.5 py-0.5 rounded text-[9px] font-black uppercase tracking-wider bg-[#FFD60A] text-black">
                            Current
                          </span>
                        )}
                      </p>
                      <p className="text-[11px] text-zinc-400 break-words">{tier.shift}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className="text-[10px] text-zinc-500 hidden sm:inline">
                      {tier.services.length} services
                    </span>
                    {expanded ? <ChevronUp className="h-4 w-4 text-zinc-400" /> : <ChevronDown className="h-4 w-4 text-zinc-400" />}
                  </div>
                </button>

                {expanded && (
                  <div className="px-4 pb-4 border-t border-zinc-800 pt-3" data-testid={`pm-package-tier-${tier.name.replace(/\s+/g, '-').toLowerCase()}-details`}>
                    <p className="text-xs italic text-zinc-400 mb-3">{tier.designedFor}</p>
                    <ul className="grid sm:grid-cols-2 gap-x-4 gap-y-1.5 mb-4">
                      {tier.services.map((s, i) => (
                        <li key={i} className="flex items-start text-xs text-white">
                          <CheckCircle className="h-3.5 w-3.5 text-[#C9A962] mr-1.5 flex-shrink-0 mt-0.5" />
                          <span>{s}</span>
                        </li>
                      ))}
                    </ul>
                    {!tier.isCurrent && (
                      <Button
                        onClick={() => setUpgradeTier(tier.name)}
                        className="font-bold"
                        size="sm"
                        style={{ backgroundColor: '#C9A962', color: '#1a1a1a' }}
                        data-testid={`pm-request-upgrade-${tier.name.replace(/\s+/g, '-').toLowerCase()}`}
                      >
                        Request Upgrade to {tier.name}
                      </Button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <p className="text-[11px] text-zinc-500 italic mt-4" data-testid="pm-package-lineup-note">
          Submitted upgrade requests route directly to the NECS Admin CRM for a revised quote and approval.
        </p>
      </Card>

      <UpgradeRequestDialog
        open={!!upgradeTier}
        onClose={() => setUpgradeTier(null)}
        property={property}
        user={user}
        businessType={businessType}
        businessLabel={businessLabel}
        currentPackage={currentPkg}
        targetPackage={upgradeTier}
      />
    </>
  );
}

function UpgradeRequestDialog({
  open, onClose, property, user, businessType, businessLabel, currentPackage, targetPackage,
}) {
  const [preferredStart, setPreferredStart] = useState('');
  const [selectedAdds, setSelectedAdds] = useState([]);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const toggleAdd = (s) => {
    setSelectedAdds((cur) => cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!preferredStart) {
      toast.error('Please choose a preferred starting date.');
      return;
    }
    setSubmitting(true);
    try {
      const specialLines = [
        `Upgrade request: ${currentPackage || 'Current'} → ${targetPackage}`,
        `Business type: ${businessLabel}`,
        selectedAdds.length ? `Alternative services requested: ${selectedAdds.join(', ')}` : '',
        notes ? `Notes: ${notes}` : '',
      ].filter(Boolean);
      const res = await fetch(`${API}/api/leads/inbound-call`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          source: 'app',
          caller_name: user?.name || user?.full_name || 'Property Manager',
          company_or_property_name: property?.property_name || '',
          property_address: property?.address || '',
          property_type: businessType === 'multifamily' ? 'multifamily' : 'commercial',
          facility_type: businessType === 'multifamily' ? '' : businessLabel,
          service_requested: [targetPackage, ...selectedAdds].filter(Boolean),
          preferred_start_date: preferredStart,
          preferred_contact_method: 'email',
          follow_up_email: user?.email || '',
          caller_phone: user?.phone || property?.phone || '',
          special_requirements: specialLines.join(' | '),
          status: 'New',
        }),
      });
      const body = await res.json();
      if (!res.ok) {
        toast.error(body.detail || 'Failed to submit upgrade request.');
        return;
      }
      toast.success(`Upgrade request submitted — NECS Admin will send a revised quote for approval.`);
      setPreferredStart('');
      setSelectedAdds([]);
      setNotes('');
      onClose();
    } catch (err) {
      toast.error('Network error — please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (!o && onClose())}>
      <DialogContent className="sm:max-w-lg" data-testid="pm-upgrade-request-dialog">
        <DialogHeader>
          <DialogTitle>
            Request Upgrade → <span style={{ color: '#C9A962' }}>{targetPackage}</span>
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="rounded-lg bg-zinc-100 p-3 text-xs text-zinc-700"
               data-testid="pm-upgrade-summary">
            <p><strong>Business type:</strong> {businessLabel}</p>
            <p><strong>Property:</strong> {property?.property_name || '—'}</p>
            <p><strong>Current package:</strong> {currentPackage || '—'}</p>
            <p><strong>Requested package:</strong> {targetPackage || '—'}</p>
          </div>

          <div>
            <Label htmlFor="pm-upgrade-start">Preferred Starting Date *</Label>
            <Input
              id="pm-upgrade-start"
              type="date"
              value={preferredStart}
              onChange={(e) => setPreferredStart(e.target.value)}
              required
              data-testid="pm-upgrade-preferred-start"
            />
          </div>

          <div>
            <Label>Alternative Services (add on top of the new package)</Label>
            <div className="grid grid-cols-2 gap-1.5 mt-1"
                 data-testid="pm-upgrade-alternative-services">
              {ALTERNATIVE_SERVICES.map((s) => {
                const active = selectedAdds.includes(s);
                return (
                  <button
                    type="button"
                    key={s}
                    onClick={() => toggleAdd(s)}
                    data-testid={`pm-upgrade-alt-${s.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
                    className={`text-left text-[11px] rounded border px-2 py-1 transition-colors ${
                      active
                        ? 'bg-[#C9A962] text-black border-[#C9A962]'
                        : 'bg-white text-zinc-700 border-zinc-300 hover:border-zinc-500'
                    }`}
                  >
                    {active ? '✓ ' : ''}{s}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <Label htmlFor="pm-upgrade-notes">Additional Notes</Label>
            <Textarea
              id="pm-upgrade-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Anything else our team should know before quoting?"
              data-testid="pm-upgrade-notes"
            />
          </div>

          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={onClose}
                    data-testid="pm-upgrade-cancel">
              Cancel
            </Button>
            <Button
              type="submit"
              className="font-bold"
              disabled={submitting}
              style={{ backgroundColor: '#C9A962', color: '#1a1a1a' }}
              data-testid="pm-upgrade-submit"
            >
              {submitting ? 'Submitting…' : 'Submit Upgrade Request'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
